import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../../../../../utils/hooks/useMutationWithError";
import { GoBackToPreviousVerificationStepAtom } from "../atoms/actions";
import { isVerifyingEmailOrPhoneAtom } from "../atoms/stores";
import { useVerifyAndAssignPhoneCodeNotificationsMutation } from "./__generated__/useVerifyAndAssignPhoneCodeNotificationsMutation.graphql";

export const useVerifyAndAssignPhoneCodeNotifications = () => {
  const [verifyAndAssignPhoneCodeNotifications, isVerifyingAndAssigning] =
    useMutationWithError<useVerifyAndAssignPhoneCodeNotificationsMutation>(
      useVerifyAndAssignPhoneCodeNotificationsMutationString
    );

  const { showToast } = useToast();
  const setVerifyingEmailOrPhone = useSetAtom(isVerifyingEmailOrPhoneAtom);
  const goBackToInitialStep = useSetAtom(GoBackToPreviousVerificationStepAtom);

  const handlePhoneCodeVerification = ({
    phoneNumber,
    code,
    onComplete,
    onError,
  }: {
    phoneNumber: string;
    code: string;
    onComplete?: () => void;
    onError: () => void;
  }) => {
    verifyAndAssignPhoneCodeNotifications({
      variables: { input: { phoneNumber, code } },
      onCompleted: (res) => {
        showToast({
          description: t`Your phone number has been updated.`,
          semanticStyle: "success",
          toastId: "phone-number-updated",
        });
        goBackToInitialStep();
        setVerifyingEmailOrPhone(null);
        if (onComplete) onComplete();
      },
      onCompletedErrorCustom(errors) {
        onError();
        return errors;
      },
    });
  };

  return [handlePhoneCodeVerification, isVerifyingAndAssigning] as const;
};

export const useVerifyAndAssignPhoneCodeNotificationsMutationString = graphql`
  mutation useVerifyAndAssignPhoneCodeNotificationsMutation(
    $input: VerifyAndAssignPhoneInput!
  ) {
    responsePayload: verifyAndAssignPhone(input: $input) {
      success
      user {
        id
        email
        ... on MemberUser {
          id
          firstName
          lastName
          email
          phoneNumber {
            nationalNumber
          }
        }
      }
      errors {
        field
        code
        message
      }
    }
  }
`;
